import { useState, useMemo, useEffect } from "react";
import { Loader2, ArrowRight, CheckCircle2, Check, Droplets, RefreshCw } from "lucide-react";
import { useApp } from "@/context/AppCtx";
import { useWallet } from "@/context/WalletContext";
import { tokenMetaCache } from "@/lib/tokenMetaCache";
import { extractHolders } from "@/lib/algo/heliusReconstruct";
import { useTranslation } from "@/lib/i18n/useTranslation";

export function ExtractAirdropPopover({ caMintInput }: { caMintInput: string }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [amount, setAmount] = useState("0.01");
  const [executing, setExecuting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [retryingId, setRetryingId] = useState<string | null>(null);
  
  const { addAppToast, globalAirdropLists, setGlobalAirdropLists } = useApp() as any;

  const handleRetry = async (list: any) => {
    const mint = list.sourceMint || list.mint || (list.id && list.id.length >= 32 ? list.id : undefined);
    if (!mint || mint.length < 32) {
      addAppToast("No valid mint address associated with this list.", "error");
      return;
    }
    
    const listId = list.id || list.mint;
    setRetryingId(listId);
    try {
      const newList = await extractHolders(mint);
      
      const updatedLists = globalAirdropLists.map((l: any) => {
        if ((l.id && l.id === list.id) || (l.mint && l.mint === list.mint)) {
          return { ...l, recipients: newList.recipients };
        }
        return l;
      });
      setGlobalAirdropLists(updatedLists);
      
      const meta = tokenMetaCache.get(mint);
      const ticker = meta?.symbol ? `$${meta.symbol}` : 'token';
      addAppToast(`Saved ${newList.recipients.length} wallets from ${ticker}`, "info");
    } catch (e: any) {
      addAppToast("Failed to extract: " + e.message, "error");
    } finally {
      setRetryingId(null);
    }
  };
  const { publicKey, secretKey } = useWallet();

  const hasWallet = !!publicKey;

  // Pre-select current chart's list if available
  useEffect(() => {
    if (isOpen && caMintInput) {
      if (!selectedListIds.includes(caMintInput) && globalAirdropLists?.some((l: any) => l.id === caMintInput || l.mint === caMintInput)) {
         setSelectedListIds(prev => [...prev, caMintInput]);
      }
    }
  }, [isOpen, caMintInput, globalAirdropLists]);

  // Combine selected lists
  const combinedRecipients = useMemo(() => {
    if (!globalAirdropLists || selectedListIds.length === 0) return [];
    
    const map = new Map<string, { amount: number, pct: number }>();
    let totalAmount = 0;
    
    selectedListIds.forEach(id => {
      const list = globalAirdropLists.find((l: any) => l.id === id || l.mint === id);
      if (list && list.recipients) {
        list.recipients.forEach((r: any) => {
           const existing = map.get(r.address) || { amount: 0, pct: 0 };
           map.set(r.address, {
             amount: existing.amount + (r.amount || 0),
             pct: existing.pct + (r.pct || 0)
           });
           totalAmount += (r.amount || 0);
        });
      }
    });
    
    const results = Array.from(map.entries()).map(([address, data]) => ({
      address,
      amount: data.amount,
      pct: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0
    })).sort((a, b) => b.pct - a.pct);
    
    return results;
  }, [globalAirdropLists, selectedListIds]);

  const handleExecute = async () => {
    if (!secretKey) {
      setErrorMsg("No local wallet generated. Please set up a wallet first.");
      return;
    }
    if (combinedRecipients.length === 0) {
      setErrorMsg("Please select at least one holder list.");
      return;
    }
    setExecuting(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const res = await fetch("/api/execute-airdrop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipients: combinedRecipients,
          amountPerWallet: amount,
          secretKeyBase58: secretKey,
          mint: caMintInput
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Airdrop failed");
      
      setSuccessMsg(`Success! ${data.bundles} Jito bundles submitted.`);
      addAppToast(`Airdropped to ${combinedRecipients.length} wallets!`, "success");
    } catch (e: any) {
      setErrorMsg(e.message);
    } finally {
      setExecuting(false);
    }
  };

  const toggleListSelection = (id: string) => {
    setSelectedListIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 rounded px-3 py-2 text-[10px] font-black uppercase transition-all shrink-0 bg-[#0a0a14]/90 backdrop-blur-md border border-white/10 text-emerald-400 hover:bg-[#0a0a14] hover:text-emerald-300 shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
      >
        <Droplets className="size-3" />
        <span>Airdrop to {combinedRecipients.length > 0 ? combinedRecipients.length : "Holders"}</span>
      </button>
    );
  }

  return (
    <div className="relative flex flex-col bg-black/80 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden w-[320px] shrink-0 text-white shadow-[0_10px_40px_rgba(0,0,0,0.8)] animate-in zoom-in-95 duration-200">
      <div className="flex items-center justify-between bg-black/40 px-3 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2 text-emerald-400">
          <Droplets className="size-4" />
          <span className="text-xs font-black uppercase tracking-wider">Airdrop Manager</span>
        </div>
        <button onClick={() => { setIsOpen(false); setSuccessMsg(""); }} className="text-white/40 hover:text-white px-2 py-1 text-[10px] uppercase font-bold transition-colors">
          Close
        </button>
      </div>

      <div className="p-3 flex flex-col gap-3 max-h-[400px] overflow-y-auto">
        <div className="text-[10px] text-white/50 uppercase tracking-widest font-bold">Select Target Lists</div>
        
        {(!globalAirdropLists || globalAirdropLists.length === 0) ? (
          <div className="text-[10px] text-white/30 italic">No holder lists found in Playground. Open a chart to auto-extract.</div>
        ) : (
          <div className="flex flex-col gap-1.5 border-b border-white/5 pb-2">
            {globalAirdropLists.map((list: any) => {
              const listId = list.id || list.mint;
              const cached = tokenMetaCache.get(listId);
              const displaySymbol = list.symbol || cached?.symbol || listId.slice(0, 4);
              const displayName = list.name || cached?.name || "Extracted Holders";
              const displayImage = list.imageUri || cached?.imageUri;
              const mint = list.sourceMint || list.mint || (list.id && list.id.length >= 32 ? list.id : undefined);
              
              return (
                <label key={listId} className="flex items-center gap-2 p-2 rounded-lg border border-white/5 bg-white/5 hover:bg-white/10 cursor-pointer transition-colors group">
                  <input 
                    type="checkbox" 
                    checked={selectedListIds.includes(listId)}
                    onChange={() => toggleListSelection(listId)}
                    className="accent-emerald-500"
                  />
                  {displayImage ? (
                    <img src={displayImage} alt={displaySymbol} className="size-6 rounded-full object-cover shadow-[0_0_10px_rgba(0,0,0,0.5)] border border-white/10" />
                  ) : (
                    <div className="size-6 rounded-full bg-white/10 flex items-center justify-center border border-white/5 shrink-0">
                      <Droplets className="size-3 text-white/40" />
                    </div>
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-[11px] font-bold text-white/90 truncate">{displayName}</span>
                    <span className="text-[9px] text-white/50 font-mono tracking-wider">${displaySymbol.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-black text-emerald-400 group-hover:text-emerald-300">{list.recipients?.length || 0}</span>
                      <span className="text-[7px] font-bold uppercase tracking-widest text-emerald-500/50">{t("airdrop.walletsCount")}</span>
                    </div>
                    {mint && (
                      <button
                        type="button"
                        disabled={retryingId === listId}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await handleRetry(list);
                        }}
                        className="p-1 rounded bg-white/5 hover:bg-emerald-500/20 text-white/60 hover:text-emerald-400 transition-colors disabled:opacity-50"
                        title={t("airdrop.retry")}
                      >
                        {retryingId === listId ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <RefreshCw className="size-3" />
                        )}
                      </button>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {combinedRecipients.length > 0 && (
           <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 text-[10px] flex justify-between items-center mb-1">
             <span className="text-emerald-400/70 font-bold uppercase">Combined Targets</span>
             <span className="text-emerald-400 font-mono font-black">{combinedRecipients.length} Wallets</span>
           </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[9px] font-black uppercase tracking-widest text-white/40">Funding Wallet (SOL)</span>
            <div className="bg-white/5 rounded-lg px-2.5 py-2 text-[10px] font-mono text-white/70 truncate flex items-center justify-between border border-white/5">
              {hasWallet ? (
                <span className="truncate pr-2">{publicKey}</span>
              ) : (
                <span className="text-rose-400">No Local Wallet Connected</span>
              )}
              {hasWallet && <Check className="size-3 text-emerald-400 shrink-0" />}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[9px] font-black uppercase tracking-widest text-white/40">Amount per wallet (Tokens)</span>
            <input 
              type="number" 
              value={amount} 
              onChange={e => setAmount(e.target.value)} 
              className="bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-emerald-400 outline-none focus:border-emerald-500/50 transition-colors shadow-inner"
              placeholder="0.01"
            />
          </div>
          
          {errorMsg && (
            <div className="text-[10px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2 font-bold leading-relaxed">
              {errorMsg}
            </div>
          )}
          
          {successMsg && (
            <div className="flex items-center gap-2 text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 font-bold">
              <CheckCircle2 className="size-4 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          <button
            onClick={handleExecute}
            disabled={executing || combinedRecipients.length === 0 || !hasWallet}
            className="mt-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-widest text-[10px] py-2.5 rounded-lg transition-all disabled:opacity-50 disabled:grayscale shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)]"
          >
            {executing ? (
              <><Loader2 className="size-3.5 animate-spin" /> Broadcasting Bundles...</>
            ) : (
              <>Execute Live Airdrop <ArrowRight className="size-3.5" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
